/**
 * The audit topics, down the left.
 *
 * A real vertical tablist rather than a list of links: the panels are all
 * part of one report, already loaded, and choosing one does not navigate
 * anywhere — which is exactly the case the WAI-ARIA Authoring Practices
 * describes tabs for. Using `orientation="vertical"` on Radix gives the
 * required semantics for free: it emits `aria-orientation`, moves selection
 * with Up/Down instead of Left/Right, and keeps a roving tabindex so the
 * whole rail is one Tab stop rather than nine.
 *
 * Radix's default `activationMode="automatic"` is kept deliberately — the APG
 * recommends activating on focus when panels are preloaded, and these are.
 *
 * On a narrow screen the rail becomes a wrapped row above the panel. Only the
 * layout changes; the roles and keyboard behaviour stay identical, which is
 * why this is a CSS reflow and not the role-swapping accordion some
 * responsive-tab write-ups recommend.
 */
import { AlertCircle, Check, HelpCircle, UserCheck } from "lucide-react";

import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { GbpChecklistItem } from "@/services/gbpAuditService";

interface Props {
	/** Ordered [key, label] pairs, as the backend declares them. */
	topics: [string, string][];
	itemsByTopic: Record<string, GbpChecklistItem[]>;
}

/** The one number worth showing beside a topic, and how urgent it is. */
function summarise(items: GbpChecklistItem[]) {
	const failed = items.filter((i) => i.status === "fail").length;
	if (failed) {
		return { count: failed, Icon: AlertCircle, tone: "text-destructive" };
	}
	const unchecked = items.filter((i) => i.status === "unknown").length;
	if (unchecked) {
		return {
			count: unchecked,
			Icon: HelpCircle,
			tone: "text-muted-foreground",
		};
	}
	const manual = items.filter((i) => i.status === "manual").length;
	if (manual) {
		return { count: manual, Icon: UserCheck, tone: "text-coral-dark" };
	}
	return {
		count: 0,
		Icon: Check,
		tone: "text-[hsl(var(--status-success))]",
	};
}

export default function GbpTopicRail({ topics, itemsByTopic }: Props) {
	return (
		<TabsList
			className="grid h-auto w-full grid-cols-2 gap-1 bg-transparent p-0 sm:grid-cols-3 md:grid-cols-1"
			aria-label="Audit topics"
		>
			{topics.map(([key, label]) => {
				const items = itemsByTopic[key] ?? [];
				const { count, Icon, tone } = summarise(items);
				return (
					<TabsTrigger
						key={key}
						value={key}
						className="h-auto justify-between gap-2 border-l-2 border-transparent px-3 py-2 text-left text-sm data-[state=active]:border-coral data-[state=active]:bg-coral-light data-[state=active]:font-medium data-[state=active]:text-coral-dark"
					>
						<span className="truncate">{label}</span>
						<span className={`flex shrink-0 items-center gap-1 ${tone}`}>
							<Icon className="h-3.5 w-3.5" />
							{count > 0 && <span className="text-xs">{count}</span>}
						</span>
					</TabsTrigger>
				);
			})}
		</TabsList>
	);
}
